//@ts-check
import React, { createContext, useState } from 'react';
import { store } from '../../../_helpers';
import { alertActions } from '../../App';
import { workflowService } from '../CustomStatus/services/workflow.service';
import { directoryService } from '../../Proof/directory.service';
import { folderWorkflowService } from './folderWorkflow.service';


const FOLDER_WORKFLOW_CONSTS = {
    PageSize: 10
}

const FolderWorkflowContext = createContext();

function FolderWorkflowProvider({ children }) {
    const [folderWorkflowConfigs, setFolderWorkflowConfigs] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [filteredTreeData, setFilteredTreeData] = useState([]);
    const [editingConfigId, setEditingConfigId] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(FOLDER_WORKFLOW_CONSTS.PageSize);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [infoModalData, setInfoModalData] = useState({
        show: false,
        title: '',
        body: '',
        secondaryButton: ''
    });

    const loadConfigs = async (page, size) => {
        try {
            const result = await folderWorkflowService.getAll({ page, pageSize: size });

            // If the requested page is now empty but data still exists,
            // fall back to the last valid page (happens after deleting the
            // last item on a page).
            if (result.list.length === 0 && result.totalCount > 0 && page > 0) {
                const lastPage = Math.max(0, result.totalPages - 1);
                setPageIndex(lastPage);
                const adjusted = await folderWorkflowService.getAll({ page: lastPage, pageSize: size });
                setFolderWorkflowConfigs(adjusted.list || []);
                setTotalPages(adjusted.totalPages || 1);
                setTotalCount(adjusted.totalCount || 0);
                return;
            }

            setFolderWorkflowConfigs(result.list || []);
            setTotalPages(result.totalPages || 1);
            setTotalCount(result.totalCount || 0);
        } catch (e) {
            console.warn('Folder workflow getAll not available yet, starting empty', e);
            setFolderWorkflowConfigs([]);
            setTotalPages(1);
            setTotalCount(0);
        }
    };

    const fetchData = async () => {
        const workflowData = await workflowService.getAll(false, true);
        const workflowOptions = (workflowData.workflows || []).map((w) => ({
            label: w.name,
            value: w.id,
            id: w.id,
            ...w
        }));
        setWorkflows(workflowOptions);

        const directories = await directoryService.getAll({});
        if (directories && directories.length > 0 && directories[0].children) {
            const rootChildren = directories[0].children.map((folder) => ({
                ...folder,
                children: [],
                isLeaf: true
            }));
            setFilteredTreeData(rootChildren);
        }

        await loadConfigs(pageIndex, pageSize);
    };

    const handlePageIndexChange = (newPageIndex) => {
        setPageIndex(newPageIndex);
        loadConfigs(newPageIndex, pageSize);
    };

    const addFolderWorkflow = async (configData) => {

        store.dispatch(alertActions.loading(true));
        try {
            await folderWorkflowService.save(configData);
            await loadConfigs(pageIndex, pageSize);
            store.dispatch(alertActions.success('Folder workflow configuration saved!'));
            return true;
        } catch (e) {
            console.error('Failed to save folder workflow config', e);
            const errorMessage = e?.message || 'Failed to save configuration';
            showInfoModal('Cannot Save Configuration', errorMessage);
            return false;
        } finally {
            store.dispatch(alertActions.loading(false));
        }
    };

    const updateFolderWorkflow = async (configData) => {
        store.dispatch(alertActions.loading(true));
        try {
            await folderWorkflowService.update(configData);
            await loadConfigs(pageIndex, pageSize);
            setEditingConfigId(null);
            store.dispatch(alertActions.success('Configuration updated!'));
            return true;
        } catch (e) {
            console.error('Failed to update folder workflow config', e);
            store.dispatch(alertActions.error('Failed to update configuration'));
            return false;
        } finally {
            store.dispatch(alertActions.loading(false));
        }
    };

    const deleteFolderWorkflow = async (config) => {
        store.dispatch(alertActions.loading(true));
        try {
            await folderWorkflowService.remove(config);
            await loadConfigs(pageIndex, pageSize);
            store.dispatch(alertActions.success('Configuration removed!'));
        } catch (e) {
            console.error('Failed to delete folder workflow config', e);
            store.dispatch(alertActions.error('Failed to remove configuration'));
        } finally {
            store.dispatch(alertActions.loading(false));
        }
    };

    const onEditStart = (config) => {
        setEditingConfigId(config.id || config.directoryId);
    };

    const onEditCancel = () => {
        setEditingConfigId(null);
    };

    const showInfoModal = (title, body) => {
        setInfoModalData({
            show: true,
            title,
            body,
            secondaryButton: ''
        });
    };

    const handleInfoModalClose = () => {
        setInfoModalData((prev) => ({ ...prev, show: false }));
    };

    const providerState = {
        folderWorkflowConfigs,
        workflows,
        filteredTreeData,
        editingConfigId,
        infoModalData,
        pageIndex,
        pageSize,
        totalPages,
        totalCount,
        fetchData,
        addFolderWorkflow,
        updateFolderWorkflow,
        deleteFolderWorkflow,
        onEditStart,
        onEditCancel,
        handleInfoModalClose,
        handlePageIndexChange
    };

    return (
        <FolderWorkflowContext.Provider value={providerState}>
            {children}
        </FolderWorkflowContext.Provider>
    );
}

const withFolderWorkflowConsumer = (Component) => {
    return function FolderWorkflowContextComponent(props) {
        return (
            <FolderWorkflowContext.Consumer>
                {(consumer) => <Component {...consumer} {...props} />}
            </FolderWorkflowContext.Consumer>
        );
    };
};

export { FolderWorkflowProvider, withFolderWorkflowConsumer };
